import { useEffect, useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useCollectionBinding,
  useProperty,
  usePropertyField,
} from '@vibesites/runtime/react';
import {
  EditableToken,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import {
  repliWhiteLogoUrl,
  MultihubLogoMonoWhite,
  FairHousingIcon,
  PetsAllowedIcon,
  WheelchairIcon,
} from '@vibesites/assets';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInstagram } from '@fortawesome/free-brands-svg-icons';
import { buildNavLinks } from '../utils/siteNav';
import { FooterNav } from './footer/FooterNav';
import {
  getContrastTextColor,
  TEXT_ON_PRIMARY_LIGHT,
} from '../utils/luminance';

// Credits bar background — matches `bg-black` below. Used to pick light vs dark
// logo variants so marks stay readable against the bar.
const CREDITS_BAR_BG = '#000000';

// ============================================================================
// Types
// ============================================================================

interface VirtualTour {
  virtual_tour_url?: string | null;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Pick a logo URL for a given background. On dark surfaces, mono_white always
 * wins; dark mono overrides from the editor are skipped. When only a dark or
 * full-color mark exists, `invert` applies a CSS filter so it reads as white.
 */
function pickContrastLogo(
  needsLightLogo: boolean,
  slotUrl: string | null | undefined,
  monoWhite: string | null | undefined,
  monoBlack: string | null | undefined,
  fullUrl?: string | null | undefined,
): { url: string | null | undefined; invert: boolean } {
  if (needsLightLogo) {
    if (monoWhite) return { url: monoWhite, invert: false };
    const slotOk = slotUrl && slotUrl !== monoBlack ? slotUrl : null;
    if (slotOk) return { url: slotOk, invert: false };
    if (fullUrl) return { url: fullUrl, invert: true };
    if (monoBlack) return { url: monoBlack, invert: true };
    return { url: null, invert: false };
  }
  if (monoBlack) return { url: monoBlack, invert: false };
  const slotOk = slotUrl && slotUrl !== monoWhite ? slotUrl : null;
  if (slotOk) return { url: slotOk, invert: false };
  return { url: fullUrl || monoWhite, invert: false };
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Footer Logo',
  fields: {
    image: {
      label: 'Logo Image',
      type: 'image',
      defaultField: 'property_primary_logo',
      suggestedFields: ['property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'],
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const instagramLinkDef: SlotGroupDefinition = {
  label: 'Instagram Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_instagram_url',
      suggestedFields: ['property_instagram_url'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const instagramImage1Def: SlotGroupDefinition = {
  label: 'Instagram Image 1',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[0].image',
      suggestedFields: ['photo_gallery[0].image'],
    },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].title' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'title' },
  },
  generator: {
    prompt: 'A standout, eye-catching photo from this property.',
    context:
      'Image 1 of 5 in the footer Instagram strip — square thumbnails in a full-width row. Pick the strongest exterior, amenity, interior, or lifestyle shot. Do not reuse images 2–5.',
  },
};

const instagramImage2Def: SlotGroupDefinition = {
  label: 'Instagram Image 2',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[1].image',
      suggestedFields: ['photo_gallery[1].image'],
    },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].title' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'title' },
  },
  generator: {
    prompt: 'Another visually striking community photo not used in image 1.',
    context:
      'Image 2 of 5 in the footer Instagram strip. Different subject and photo from image 1. Do not reuse images 3–5.',
  },
};

const instagramImage3Def: SlotGroupDefinition = {
  label: 'Instagram Image 3',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[2].image',
      suggestedFields: ['photo_gallery[2].image'],
    },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].title' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'title' },
  },
  generator: {
    prompt: 'A third standout photo that showcases something different from images 1 and 2.',
    context:
      'Image 3 of 5 in the footer Instagram strip. Different photo from images 1–2. Do not reuse images 4–5.',
  },
};

const instagramImage4Def: SlotGroupDefinition = {
  label: 'Instagram Image 4',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[3].image',
      suggestedFields: ['photo_gallery[3].image'],
    },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[3].title' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 3,
    fieldMappings: { image: 'image', alt: 'title' },
  },
  generator: {
    prompt: 'A fourth eye-catching photo with a fresh angle on the community.',
    context:
      'Image 4 of 5 in the footer Instagram strip. Different photo from images 1–3. Do not reuse image 5.',
  },
};

const instagramImage5Def: SlotGroupDefinition = {
  label: 'Instagram Image 5',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[4].image',
      suggestedFields: ['photo_gallery[4].image'],
    },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[4].title' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 4,
    fieldMappings: { image: 'image', alt: 'title' },
  },
  generator: {
    prompt: 'A fifth standout photo that rounds out the strip with something not shown in images 1–4.',
    context:
      'Image 5 of 5 in the footer Instagram strip. Different photo from images 1–4.',
  },
};

const scheduleTourLinkDef: SlotGroupDefinition = {
  label: 'Schedule Tour Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Visit' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const managedByLogoDef: SlotGroupDefinition = {
  label: 'Managed By Logo',
  fields: {
    image: {
      label: 'Logo Image',
      type: 'image',
      defaultField: 'property_company_mono_white_logo',
      suggestedFields: [
        'property_company_mono_white_logo',
        'property_company_mono_black_logo',
        'property_company_logo',
      ],
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Property Management Company Logo' },
    url: { label: 'URL', type: 'url', defaultField: 'property_company_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const poweredByLogoDef: SlotGroupDefinition = {
  label: 'Powered By Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Multihub' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.multihub.io/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const designByLogoDef: SlotGroupDefinition = {
  label: 'Website Design By Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Repli Logo' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.repli360.com/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const accessibilityLinkDef: SlotGroupDefinition = {
  label: 'Accessibility Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Accessibility' },
    url: { label: 'URL', type: 'url', defaultValue: '/accessibility' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const privacyPolicyLinkDef: SlotGroupDefinition = {
  label: 'Privacy Policy Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Privacy Policy' },
    url: { label: 'URL', type: 'url', defaultValue: '/privacy-policy' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const sitemapLinkDef: SlotGroupDefinition = {
  label: 'Sitemap Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Sitemap' },
    url: { label: 'URL', type: 'url', defaultValue: '/sitemap' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Component
// ============================================================================

export function Footer() {
  const propertyName = useContentToken('footer.property_name');

  const logo = useSlotGroup('footer.logo', logoSlotDef);
  const monoBlackLogo = usePropertyField('property_mono_black_logo');
  const logoImage = logo.image || monoBlackLogo;

  const instagramLink = useSlotGroup('footer.instagram_link', instagramLinkDef);
  const scheduleTourLink = useSlotGroup('footer.schedule_tour_link', scheduleTourLinkDef);

  // Pick mono_white vs mono_black (and Repli white vs black) based on the
  // credits-bar background luminance — mirrors Header/Footer contrast logic.
  const useLightLogoOnCredits =
    getContrastTextColor(CREDITS_BAR_BG) === TEXT_ON_PRIMARY_LIGHT;

  const runtimeManagedByLogoDef: SlotGroupDefinition = useLightLogoOnCredits
    ? {
        ...managedByLogoDef,
        fields: {
          ...managedByLogoDef.fields,
          image: {
            ...managedByLogoDef.fields.image,
            defaultField: 'property_company_mono_white_logo',
          },
        },
      }
    : {
        ...managedByLogoDef,
        fields: {
          ...managedByLogoDef.fields,
          image: {
            ...managedByLogoDef.fields.image,
            defaultField: 'property_company_mono_black_logo',
          },
        },
      };

  const managedByLogo = useSlotGroup('footer.managed_by_logo', runtimeManagedByLogoDef);
  const companyMonoWhite = usePropertyField('property_company_mono_white_logo');
  const companyMonoBlack = usePropertyField('property_company_mono_black_logo');
  const companyLogo = usePropertyField('property_company_logo');
  const managedByLogoPick = pickContrastLogo(
    useLightLogoOnCredits,
    managedByLogo.image,
    companyMonoWhite,
    companyMonoBlack,
    companyLogo,
  );
  const managedByImageUrl = managedByLogoPick.url;
  const managedByInvert = managedByLogoPick.invert;
  const repliLogoUrl = repliWhiteLogoUrl;

  const poweredByLogo = useSlotGroup('footer.powered_by_logo', poweredByLogoDef);
  const designByLogo = useSlotGroup('footer.design_by_logo', designByLogoDef);
  const accessibilityLink = useSlotGroup('footer.accessibility_link', accessibilityLinkDef);
  const privacyPolicyLink = useSlotGroup('footer.privacy_policy_link', privacyPolicyLinkDef);
  const sitemapLink = useSlotGroup('footer.sitemap_link', sitemapLinkDef);

  const instagramImage1 = useSlotGroup('footer.instagram_image_1', instagramImage1Def);
  const instagramImage2 = useSlotGroup('footer.instagram_image_2', instagramImage2Def);
  const instagramImage3 = useSlotGroup('footer.instagram_image_3', instagramImage3Def);
  const instagramImage4 = useSlotGroup('footer.instagram_image_4', instagramImage4Def);
  const instagramImage5 = useSlotGroup('footer.instagram_image_5', instagramImage5Def);

  // Navigation — mirrors Header visibility rules
  const virtualTours = useCollectionBinding<VirtualTour>('footer.virtual_tours', { collection: 'virtual_tours' });
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);

  const propertyForLeasing = useProperty() as Record<string, unknown>;
  const apartmentSyncJsId = propertyForLeasing?.custom_apartmentsync_javascript_id as string | null | undefined;
  const apartmentSyncConfigId = propertyForLeasing?.custom_apartmentsync_config_id as string | null | undefined;
  const interactiveMapShowIfExists = useContentToken('footer.interactive_map_show_if_exists');
  const hasInteractiveMapOverride = !!(interactiveMapShowIfExists && String(interactiveMapShowIfExists).trim());
  const hasInteractiveLeasing =
    (!!(apartmentSyncJsId && String(apartmentSyncJsId).trim()) &&
      !!(apartmentSyncConfigId && String(apartmentSyncConfigId).trim())) ||
    hasInteractiveMapOverride;

  const navLinks = useMemo(
    () => buildNavLinks(hasVirtualTours, hasInteractiveLeasing, { includeHome: true }),
    [hasVirtualTours, hasInteractiveLeasing],
  );

  // ─── RentCafe IQ chatbot z-index pin ────────────────────────────────────────
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const PATTERNS = [
      /\brcc-app/i,
      /\bwebchat-toggle/i,
      /conversations-webchat/i,
      /rentcafe/i,
      /rciq/i,
      /\bcrm[-_ ]?iq\b/i,
      /\biq[-_ ]?(chat|bot)/i,
      /yardi/i,
      /yvenue/i,
    ];

    const Z = '2147483647';

    function attrHaystack(el: Element): string {
      const id = el.id || '';
      const cls = typeof el.className === 'string'
        ? el.className
        : ((el.className as unknown as { baseVal?: string })?.baseVal || '');
      const src = el.getAttribute('src') || '';
      const title = el.getAttribute('title') || '';
      const name = el.getAttribute('name') || '';
      let data = '';
      for (const a of Array.from(el.attributes)) {
        if (a.name.startsWith('data-')) data += ` ${a.name} ${a.value}`;
      }
      return `${id} ${cls} ${src} ${title} ${name}${data}`;
    }

    function looksLikeRciq(el: Element): boolean {
      return PATTERNS.some((p) => p.test(attrHaystack(el)));
    }

    function outermostFixedAncestor(el: Element): HTMLElement | null {
      let cur: Element | null = el;
      let last: HTMLElement | null = null;
      while (cur && cur !== document.body && cur instanceof HTMLElement) {
        if (getComputedStyle(cur).position === 'fixed') last = cur;
        cur = cur.parentElement;
      }
      return last;
    }

    function pin(el: HTMLElement) {
      el.style.setProperty('z-index', Z, 'important');
    }

    function processCandidate(el: Element) {
      if (!(el instanceof HTMLElement)) return;
      if (!looksLikeRciq(el)) return;
      pin(el);
      const fixedAncestor = outermostFixedAncestor(el);
      if (fixedAncestor && fixedAncestor !== el) pin(fixedAncestor);
    }

    function scanSubtree(root: ParentNode) {
      const candidates = root.querySelectorAll('iframe, div, aside, section, button');
      for (const c of Array.from(candidates)) processCandidate(c);
      if (root instanceof Element) processCandidate(root);
    }

    scanSubtree(document.body);

    let scheduled = false;
    const observer = new MutationObserver((records) => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        for (const r of records) {
          for (const node of Array.from(r.addedNodes)) {
            if (node instanceof Element) scanSubtree(node);
          }
          if (r.type === 'attributes' && r.target instanceof Element) {
            processCandidate(r.target);
          }
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['id', 'class', 'src', 'title', 'name', 'style'],
    });

    return () => observer.disconnect();
  }, []);

  const legalLinkClass =
    'font-body text-[11px] uppercase tracking-wider text-white/80 hover:text-white transition-colors';

  return (
    <footer>
      {/* ─── Instagram feed strip ─────────────────────────────────────────── */}
      <div style={{ backgroundColor: 'var(--vibe-color-primary)' }}>
        <div className="py-4 text-center">
          <EditableSlotGroup groupPath="footer.instagram_link" definition={instagramLinkDef}>
            <a
              href={instagramLink.url || '#'}
              target={instagramLink._target || '_blank'}
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 sm:gap-3 font-body text-[20px] sm:text-[28px] md:text-[35px] font-semibold uppercase tracking-[0.15em] text-white hover:text-white/80 transition-colors leading-none"
            >
              <RichTextContent
                path="footer.follow_us_label"
                defaultValue="<p>Follow Us On</p>"
                as="span"
                className="text-[20px] sm:text-[28px] md:text-[35px] leading-none"
              />
              <FontAwesomeIcon icon={faInstagram} className="w-5 h-5 sm:w-7 sm:h-7 md:w-8 md:h-8" aria-hidden="true" />
            </a>
          </EditableSlotGroup>
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-5 gap-0">
          <EditableSlotGroup groupPath="footer.instagram_image_1" definition={instagramImage1Def}>
            <div className="aspect-square overflow-hidden bg-white/5">
              {instagramImage1.image ? (
                <VibesitesImage
                  url={instagramImage1.image}
                  alt={instagramImage1.alt || 'Community photo'}
                  className="w-full h-full object-cover"
                  sizes="(min-width: 640px) 20vw, 33vw"
                />
              ) : null}
            </div>
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="footer.instagram_image_2" definition={instagramImage2Def}>
            <div className="aspect-square overflow-hidden bg-white/5">
              {instagramImage2.image ? (
                <VibesitesImage
                  url={instagramImage2.image}
                  alt={instagramImage2.alt || 'Community photo'}
                  className="w-full h-full object-cover"
                  sizes="(min-width: 640px) 20vw, 33vw"
                />
              ) : null}
            </div>
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="footer.instagram_image_3" definition={instagramImage3Def}>
            <div className="aspect-square overflow-hidden bg-white/5">
              {instagramImage3.image ? (
                <VibesitesImage
                  url={instagramImage3.image}
                  alt={instagramImage3.alt || 'Community photo'}
                  className="w-full h-full object-cover"
                  sizes="(min-width: 640px) 20vw, 33vw"
                />
              ) : null}
            </div>
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="footer.instagram_image_4" definition={instagramImage4Def}>
            <div className="hidden sm:block aspect-square overflow-hidden bg-white/5">
              {instagramImage4.image ? (
                <VibesitesImage
                  url={instagramImage4.image}
                  alt={instagramImage4.alt || 'Community photo'}
                  className="w-full h-full object-cover"
                  sizes="(min-width: 640px) 20vw, 33vw"
                />
              ) : null}
            </div>
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="footer.instagram_image_5" definition={instagramImage5Def}>
            <div className="hidden sm:block aspect-square overflow-hidden bg-white/5">
              {instagramImage5.image ? (
                <VibesitesImage
                  url={instagramImage5.image}
                  alt={instagramImage5.alt || 'Community photo'}
                  className="w-full h-full object-cover"
                  sizes="(min-width: 640px) 20vw, 33vw"
                />
              ) : null}
            </div>
          </EditableSlotGroup>
        </div>
      </div>

      {/* ─── White navigation bar ─────────────────────────────────────────── */}
      <div className="bg-white border-b border-[var(--vibe-color-border)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
          <div className="flex flex-col md:flex-row items-center gap-6 md:gap-4">
            {/* Logo */}
            <div className="flex-shrink-0 md:w-auto">
              <EditableSlotGroup groupPath="footer.logo" definition={logoSlotDef}>
                <a href="/" className="block">
                  {logoImage ? (
                    <VibesitesImage
                      url={logoImage}
                      alt={logo.alt || 'Community Logo'}
                      className="h-16 md:h-20 w-auto max-w-[220px] object-contain"
                    />
                  ) : (
                    <div className="font-heading text-3xl text-[var(--vibe-color-text)] uppercase tracking-wide">
                      {propertyName}
                    </div>
                  )}
                </a>
              </EditableSlotGroup>
            </div>

            {/* Nav links */}
            <FooterNav navLinks={navLinks} />

            {/* Schedule a Visit CTA */}
            <div className="flex-shrink-0 w-full md:w-auto md:ml-auto flex justify-center">
              <EditableSlotGroup groupPath="footer.schedule_tour_link" definition={scheduleTourLinkDef}>
                <a
                  href={scheduleTourLink.url || '/schedule-a-tour'}
                  target={scheduleTourLink._target || '_self'}
                  className="inline-block px-6 py-3 font-body text-[13px] font-semibold uppercase tracking-wider text-white transition-opacity hover:opacity-90 whitespace-nowrap"
                  style={{ backgroundColor: 'var(--vibe-color-primary)' }}
                >
                  {scheduleTourLink.label || 'Schedule a Visit'}
                </a>
              </EditableSlotGroup>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Black credits & legal bar ────────────────────────────────────── */}
      <div className="bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Row on tablet & desktop: managed-by (left), center content, design-by (right) */}
          <div className="flex flex-col items-center gap-8 md:grid md:grid-cols-[1fr_auto_1fr] md:items-center md:gap-6">
            {/* Left: Professionally Managed By */}
            <EditableSlotGroup
              groupPath="footer.managed_by_logo"
              definition={runtimeManagedByLogoDef}
              className="flex flex-col items-center md:items-start gap-2"
            >
              <RichTextContent
                path="footer.managed_by_text"
                defaultValue="<p>Professionally Managed by</p>"
                as="span"
                className="font-body text-[10px] uppercase tracking-wider text-white/70 md:whitespace-nowrap"
              />
              {managedByImageUrl ? (
                <a
                  href={managedByLogo.url || '#'}
                  target={managedByLogo._target || '_blank'}
                  rel="noopener noreferrer"
                  className="opacity-90 hover:opacity-100 transition-opacity"
                >
                  <VibesitesImage
                    url={managedByImageUrl}
                    alt={managedByLogo.alt || 'Property Management Company'}
                    className={`h-7 w-auto max-w-[160px] object-contain${managedByInvert ? ' brightness-0 invert' : ''}`}
                    sizes="160px"
                  />
                </a>
              ) : null}
            </EditableSlotGroup>

            {/* Center: icons, copyright, legal links. On mobile it moves to the top so
                the credit logos come last; on tablet/desktop it sits in the middle. */}
            <div className="flex flex-col items-center gap-3 order-first md:order-none">
              <div className="flex items-center justify-center gap-5 text-white/70">
                <FairHousingIcon size={24} color="currentColor" />
                <PetsAllowedIcon size={24} color="currentColor" />
                <WheelchairIcon size={24} color="currentColor" />
              </div>

              <p className="font-body text-[11px] text-white/70 text-center whitespace-nowrap">
                © {new Date().getFullYear()} All Rights Reserved |{' '}
                <EditableToken path="footer.property_name">
                  {propertyName}
                </EditableToken>
              </p>

              <nav className="grid grid-cols-[1fr_auto_1fr] items-center gap-x-3" aria-label="Legal links">
                <div className="flex items-center justify-end gap-x-3">
                  <EditableSlotGroup groupPath="footer.sitemap_link" definition={sitemapLinkDef}>
                    <a href={sitemapLink.url || '/sitemap'} target={sitemapLink._target || '_self'} className={legalLinkClass}>
                      {sitemapLink.text || 'Sitemap'}
                    </a>
                  </EditableSlotGroup>
                  <span className="text-white/30" aria-hidden="true">|</span>
                </div>
                <EditableSlotGroup groupPath="footer.privacy_policy_link" definition={privacyPolicyLinkDef}>
                  <a href={privacyPolicyLink.url || '/privacy-policy'} target={privacyPolicyLink._target || '_self'} className={legalLinkClass}>
                    {privacyPolicyLink.text || 'Privacy Policy'}
                  </a>
                </EditableSlotGroup>
                <div className="flex items-center justify-start gap-x-3">
                  <span className="text-white/30" aria-hidden="true">|</span>
                  <EditableSlotGroup groupPath="footer.accessibility_link" definition={accessibilityLinkDef}>
                    <a href={accessibilityLink.url || '/accessibility'} target={accessibilityLink._target || '_self'} className={legalLinkClass}>
                      {accessibilityLink.text || 'Accessibility'}
                    </a>
                  </EditableSlotGroup>
                </div>
              </nav>
            </div>

            {/* Right: Powered By + Website Design By */}
            <div className="flex flex-col sm:flex-row items-center sm:items-start md:justify-end gap-8">
              <EditableSlotGroup
                groupPath="footer.powered_by_logo"
                definition={poweredByLogoDef}
                className="flex flex-col items-center md:items-end gap-2"
              >
                <RichTextContent
                  path="footer.powered_by_text"
                  defaultValue="<p>Powered by</p>"
                  as="span"
                  className="font-body text-[10px] uppercase tracking-wider text-white/70 leading-none md:whitespace-nowrap"
                />
                <a
                  href={poweredByLogo.url || 'https://www.multihub.io/'}
                  target={poweredByLogo._target || '_blank'}
                  rel="noopener noreferrer"
                  className="opacity-90 hover:opacity-100 transition-opacity"
                >
                  <MultihubLogoMonoWhite height={16} alt={poweredByLogo.alt || 'Multihub'} />
                </a>
              </EditableSlotGroup>

              <EditableSlotGroup
                groupPath="footer.design_by_logo"
                definition={designByLogoDef}
                className="flex flex-col items-center md:items-end gap-2"
              >
                <RichTextContent
                  path="footer.design_by_text"
                  defaultValue="<p>Website Design by</p>"
                  as="span"
                  className="font-body text-[10px] uppercase tracking-wider text-white/70 leading-none"
                />
                <a
                  href={designByLogo.url || 'https://www.repli360.com/'}
                  target={designByLogo._target || '_blank'}
                  rel="noopener noreferrer"
                  className="opacity-90 hover:opacity-100 transition-opacity"
                >
                  <VibesitesImage
                    url={repliLogoUrl}
                    alt={designByLogo.alt || 'Repli Logo'}
                    height={24}
                    className="h-6 w-auto"
                    sizes="100px"
                    priority
                  />
                </a>
              </EditableSlotGroup>
            </div>
          </div>
        </div>
      </div>

      {/* Editor-only: Interactive Map nav override token */}
      <EditableToken
        path="footer.interactive_map_show_if_exists"
        type="text"
        label="Interactive Map: Show If Exists"
        description="Expose the Interactive Map page in footer navigation if this token or field contains a value."
        as="span"
        className="sr-only"
      >
        {interactiveMapShowIfExists || ''}
      </EditableToken>

      {/* RentCafe IQ z-index fallback */}
      <style>{`
        .rcc-app-cta-wrap,
        .rcc-app-controls,
        [class*="rcc-app-"],
        [id*="conversations-webchat"],
        [class*="webchat-toggle"],
        iframe[src*="rentcafeiq"],
        iframe[src*="rentcafe-iq"],
        iframe[src*="rentcafe.com/iq"],
        iframe[src*="yardi"],
        iframe[src*="yvenue"],
        iframe[title*="RentCafe IQ" i],
        iframe[title*="Rent Cafe IQ" i],
        iframe[title*="CRM IQ" i],
        iframe[title*="webchat" i],
        [id^="rciq"],
        [id*="rentcafeiq"],
        [id*="rentcafe-iq"],
        [id*="rentcafe_iq"],
        [id*="iq-chat"],
        [id*="iqchat"],
        [id*="iq-bot"],
        [id*="iqbot"],
        [class^="rciq-"],
        [class*=" rciq-"],
        [class*="rentcafeiq"],
        [class*="rentcafe-iq"],
        [class*="rentcafe_iq"],
        [class*="iq-chat"],
        [class*="iqchat"],
        [class*="yardi-chat"],
        [class*="yvenue"],
        [data-rciq],
        [data-rentcafeiq] {
          z-index: 2147483647 !important;
        }
      `}</style>
    </footer>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Footer',
  displayName: 'Footer',
  tokens: {
    'footer.property_name': {
      label: 'Property Name',
      type: 'text',
      defaultField: 'property_name',
      defaultValue: 'Our Community',
    },
    'footer.follow_us_label': {
      label: 'Follow Us Label',
      type: 'richtext',
      defaultValue: '<p>Follow Us On</p>',
      generator: {
        prompt: 'Short social call-to-action inviting visitors to follow on Instagram.',
        context:
          'Centered label above the footer Instagram photo strip, linked to the property Instagram profile.',
        length: '2–4 words',
      },
    },
    'footer.managed_by_text': {
      label: 'Managed By Text',
      type: 'richtext',
      defaultValue: '<p>Professionally Managed by</p>',
    },
    'footer.powered_by_text': {
      label: 'Powered By Text',
      type: 'richtext',
      defaultValue: '<p>Powered by</p>',
    },
    'footer.design_by_text': {
      label: 'Website Design By Text',
      type: 'richtext',
      defaultValue: '<p>Website Design by</p>',
    },
    'footer.interactive_map_show_if_exists': {
      label: 'Interactive Map: Show If Exists',
      type: 'text',
      description: 'Expose the Interactive Map page in footer navigation if this token or field contains a value.',
      defaultValue: '',
    },
  },
  slotGroups: {
    'footer.logo': logoSlotDef,
    'footer.instagram_link': instagramLinkDef,
    'footer.instagram_image_1': instagramImage1Def,
    'footer.instagram_image_2': instagramImage2Def,
    'footer.instagram_image_3': instagramImage3Def,
    'footer.instagram_image_4': instagramImage4Def,
    'footer.instagram_image_5': instagramImage5Def,
    'footer.schedule_tour_link': scheduleTourLinkDef,
    'footer.managed_by_logo': managedByLogoDef,
    'footer.powered_by_logo': poweredByLogoDef,
    'footer.design_by_logo': designByLogoDef,
    'footer.accessibility_link': accessibilityLinkDef,
    'footer.privacy_policy_link': privacyPolicyLinkDef,
    'footer.sitemap_link': sitemapLinkDef,
  },
  collectionBindings: {
    'footer.virtual_tours': {
      label: 'Virtual Tours (nav visibility)',
      collection: 'virtual_tours',
    },
  },
};

(Footer as any).contentSchema = contentSchema;

export default Footer;
